import {createContext, useContext, useEffect, useState} from 'react';
import {
  getFirestore,
  onSnapshot,
  collection,
  getDocs,
  query,
  where,
  updateDoc,
} from 'firebase/firestore';
import {useParams} from 'react-router-dom';
import {z} from 'zod';
import {ONE_HOUR_MS, ONE_MINUTE_MS, useServerTime} from './TimeContext';
import {Role, RoleFirebaseString} from 'types/role';
import type {CourseId} from 'types/common';
import {
  getCourseDocumentRef,
  type MissedSectionMode,
} from '@cip/core/firebase/client';
import {
  DEFAULT_LESSON_GROUPS,
  resolveCourseCalendarConfig,
  resolveHiddenRichTextTools,
  resolveLessonGroups,
  resolveLockedPages,
  resolveLockedPagesOpenToSls,
  SECTION_FEEDBACK_DELAY_DAYS_DEFAULT,
  SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT,
  STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT,
} from '@cip/core/firebase';
import type {
  CourseFeature,
  HideableRichTextTool,
  LessonGroup,
  LockableCoursePage,
  ResolvedCourseCalendarConfig,
} from '@cip/core/firebase';
import {type CourseWeekId, type SectionWeekId} from '@cip/core/section';
import {useCourseWeeksInternal} from './useWeekHooks';
import type {LanguageCode} from 'components/translation/utils';
import {useSetCourseName} from './accessibility/AccessibilityContext';
import {firebaseStringToRole, roleToFirebaseString} from 'contexts/profileUtil';

const RoadmapIconsSchema = z.record(z.string(), z.string());

/**
 * Reads the course document's `roadmapIcons` map, tolerating a missing or
 * malformed field.
 *
 * @param value - The raw field value.
 * @return The map, or an empty object when absent or malformed.
 * @throws Never.
 */
function parseRoadmapIcons(value: unknown): Record<string, string> {
  const parsed = RoadmapIconsSchema.safeParse(value);
  return parsed.success ? parsed.data : {};
}

/**
 * The visual/behavioural style of a course. Controls UI chrome, branding, and
 * feature defaults throughout the app.
 *
 * - `'cip'`  — Code in Place (default when the field is absent in Firestore).
 * - `'math'` — Math 4 AI and similar maths-focused courses.
 */
export type CourseStyle = 'cip' | 'math';

/** Returns a valid {@link CourseStyle}, defaulting to `'cip'` when unrecognised. */
function parseCourseStyle(raw: unknown): CourseStyle {
  return raw === 'math' ? 'math' : 'cip';
}

export interface CourseContextType {
  /**
   * The id of the course.
   */
  courseId: CourseId;
  /**
   * Whether the course data is loading.
   */
  isLoading: boolean;
  /**
   * The error that occurred when loading the course data (if any).
   */
  error: Error | null;
  /**
   * Whether the course document exists. False until `isLoading` clears, so
   * check `isLoading` first to tell "not yet known" from "no such course".
   */
  courseExists: boolean;
  /**
   * The minimum role the course requires of its members, from the course
   * document's `minRole`. `Role.UNREGISTERED` when the course sets none,
   * which is also the value that applies no gate.
   */
  courseMinRole: Role;
  /**
   * The (human-readable) name of the course.
   */
  courseName: string;
  /**
   * The start date of the course. Formatted as an ISO 8601 string as
   * YYYY-MM-DDTHH:mm:ssXXX
   */
  courseStartDate: string;
  /**
   * The end date of the course. Formatted as an ISO 8601 string as
   * YYYY-MM-DDTHH:mm:ssXXX
   */
  courseEndDate: string;
  /**
   * The number of weeks in the course.
   */
  nCourseWeeks: number;
  /**
   * The current week of the course.
   */
  _courseLessonWeek: CourseWeekId;
  /**
   * The current week of the section.
   */
  _courseSectionWeek: SectionWeekId;
  /**
   * The timestamp of the first section of the course. Section start times are
   * calculated from this timestamp and the section time delta. Note that the
   * timezone of this field may not be specified and is hardcoded to UTC-7
   * (PDT). Formatted as an ISO 8601 string as YYYY-MM-DDTHH:mm:ss-07:00
   */
  firstSectionTimestamp: string;
  /**
   * The number of hours of section a week. In other words, the difference in
   * hours between the start of the first section and the start of the last
   * section in a week.
   */
  sectionTimeDelta: number;
  /**
   * Days after a week's last section ENDS before SL section feedback opens.
   * Course `sectionFeedbackDelayDays`, defaulting to
   * `SECTION_FEEDBACK_DELAY_DAYS_DEFAULT`.
   */
  sectionFeedbackDelayDays: number;
  /**
   * Minutes after a section's start during which a student may still join it
   * for the first time. Course `studentJoinGraceMinutesAfterStart`,
   * defaulting to `STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT`.
   */
  studentJoinGraceMinutesAfterStart: number;
  /**
   * The weekday at whose end peer-shadow reviews of a week are due, numbered as
   * `Date.prototype.getDay` numbers weekdays (see `getShadowReviewDueMs`).
   * Course `shadowReviewDueWeekday`, defaulting to
   * `SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT`.
   */
  shadowReviewDueWeekday: number;
  /**
   * Whether a feature is enabled for the course. By default, checks through the
   * student features; if isSl is true, checks through the section leader
   * features.
   */
  isFeatureEnabled: (featureName: CourseFeature, isSl?: boolean) => boolean;
  /**
   * Fetches the sections for the course.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  getSectionsSnapshot: () => any;
  /**
   * Gets the first day of a section.
   *
   * @deprecated Prefer `computeSectionInstant` from `@cip/core/section`
   *   with `{firstSectionTimestamp, timeIndex: sectionIndex, weekIndex: 0}`.
   *
   *   TODO: [#2364](https://github.com/CodeinPlace/cip/issues/2364) -
   *   migrate callers to `computeSectionInstant` and drop this from
   *   the context.
   */
  getFirstDayOfSection: (sectionIndex: number) => Date;
  /**
   * Gets the Date of the start first section (course-wide) for a given section
   * week. Typically a Wednesday at 3 PM PDT.
   *
   * @deprecated Prefer `computeSectionInstant` from `@cip/core/section`
   *   with `{firstSectionTimestamp, timeIndex: 0, weekIndex: sectionWeekId - 1}`.
   *
   *   TODO: [#2364](https://github.com/CodeinPlace/cip/issues/2364) -
   *   migrate callers and drop this from the context.
   */
  getFirstSectionOfWeekDate: (sectionWeekId: SectionWeekId) => Date;
  /**
   * Gets the Date of the start of the last section (course-wide) for a given
   * section week. Typically a Friday at 3 PM PDT.
   *
   * @deprecated Prefer `computeSectionInstant` plus an explicit
   *   `sectionTimeDelta - 1` hour offset.
   *
   *   TODO: [#2364](https://github.com/CodeinPlace/cip/issues/2364) -
   *   migrate callers and drop this from the context.
   */
  getLastSectionOfWeekDate: (sectionWeekId: SectionWeekId) => Date;
  /**
   * Gets the Date of the start of the last section (course-wide) for a given
   * section week. Typically a Friday at 3 PM PDT.
   */
  getLastSectionOfCourseDate: () => Date;
  /**
   * The features enabled for the course.
   */
  courseFeatures: CourseFeature[];
  /**
   * The features enabled for the section leaders.
   */
  slFeatures: CourseFeature[];
  /**
   * The typesense index for the stories feature.
   */
  storiesTypesenseIndex: string;
  /**
   * The list of roles that have access to a roadmap for the course.
   */
  roadmapList: RoleFirebaseString[];
  /**
   * Course-wide default roadmap icon per item type (see `RoadmapItemIcon`).
   * Empty when the course has not customised any.
   */
  roadmapIcons: Record<string, string>;
  /**
   * Replaces the course-wide default roadmap icons in Firestore. Rejects
   * when the write fails.
   */
  updateRoadmapIcons: (icons: Record<string, string>) => Promise<void>;
  /**
   * The headings of the student lessons page in display order, and the lesson
   * types the lesson editor offers. See `resolveLessonGroups` in `@cip/core`.
   */
  lessonGroups: readonly LessonGroup[];
  /**
   * Pages whose sidebar entry reads "Coming soon" and whose route is closed
   * to non-staff, except section leaders on the pages in
   * `lockedPagesOpenToSls`. See `resolveLockedPages` in `@cip/core`.
   */
  lockedPages: readonly LockableCoursePage[];
  /**
   * The locked pages section leaders can still open. See
   * `resolveLockedPagesOpenToSls` in `@cip/core`.
   */
  lockedPagesOpenToSls: readonly LockableCoursePage[];
  /**
   * The link to use to authenticate with Canvas (if applicable).
   */
  courseCanvasAuthLink: string;
  /**
   * TODO: Remove this field. Only used by CS 105 to determine which instructors
   * are in the course. Instead, instructors should be fetched from a new
   * field.
   */
  isCodeInPlaceCourse: boolean;
  /**
   * Whether Karel is used in the course.
   */
  usesKarel: boolean;
  /**
   * Whether TeachNow is used in the course.
   */
  usesTeachNow: boolean;
  /**
   * Whether sections use an instructor-provided ("bring your own") Zoom link
   * instead of CIP-managed meeting creation. See `usesExternalZoom` on the
   * course document.
   */
  usesExternalZoom: boolean;
  /**
   * Whether a section leader's Join Section button opens the week's slide
   * deck in a new tab before redirecting to Zoom. See
   * `opensSlidesOnSectionJoin` on the course document.
   */
  opensSlidesOnSectionJoin: boolean;
  /** Firestore ids of the course's configured Super Section records. */
  superSectionIds: string[];
  /**
   * UIDs on the course's Super Section leader roster. Grants nothing by
   * itself; see `getSuperSectionAccess`.
   */
  superSectionLeaderIds: string[];
  /**
   * Whether section leaders can refer students during onboarding. See
   * `allowSlsToReferStudents` on the course document.
   */
  allowSlsToReferStudents: boolean;
  /**
   * Whether rich text editors in this course offer the equation editor
   * (insert button + click-to-edit). See `equationEditorEnabled` on the
   * course document.
   */
  equationEditorEnabled: boolean;
  /**
   * Toolbar buttons rich text editors in this course leave out. See
   * `resolveHiddenRichTextTools` in `@cip/core`.
   */
  hiddenRichTextTools: readonly HideableRichTextTool[];
  /**
   * Id of the main-forum post holding the forum guidelines, or `undefined`
   * when the course names none. See `forumGuidelinesPostId` on the course
   * document.
   */
  forumGuidelinesPostId: string | undefined;
  /**
   * Whether the course has mentors; false hides the mentor pages from the
   * sidebar. See `hasMentors` on the course document.
   */
  hasMentors: boolean;
  /**
   * Whether the course has grades.
   */
  courseHasGrades: boolean;
  /**
   * Whether the course is a Foothill College course.
   */
  isFoothill: boolean;
  /**
   * The visual/behavioural style of the course. Controls UI chrome, branding,
   * and feature defaults. Defaults to `'cip'` when not set in Firestore.
   * See {@link CourseStyle} for valid values.
   */
  courseStyle: CourseStyle;
  /**
   * The course's IANA timezone from the course document, or an empty string
   * when unset.
   */
  courseTimezone: string;
  /**
   * Home-page calendar settings with defaults applied. See
   * `resolveCourseCalendarConfig` in `@cip/core`.
   */
  calendarConfig: ResolvedCourseCalendarConfig;
  /**
   * The course's IDE style-feedback exclusion list, exactly as stored. Typed
   * `unknown` because nothing validates it on read, and reading it as a list
   * when it holds something else changes behavior silently — pass it to
   * `isStyleFeedbackAvailableForAssignment`, which parses it and falls back to
   * the built-in defaults. Consumers should go through
   * `useStyleFeedbackAvailability` rather than read this directly.
   */
  styleFeedbackExcludedAssignments: unknown;
  /**
   * The course that hosts this course's application materials, exactly as
   * stored on the course document (`parentCourseId` in `@cip/core`'s course
   * schema). Undefined when the field is absent or the document has not
   * loaded. Resolve through `useParentCourseId` rather than reading this
   * directly, so the legacy fallback for older course documents applies.
   */
  parentCourseId?: CourseId;
  /**
   * The number of students allowed to be permanently assigned to a minor
   * section.
   */
  minorSectionCap: number;
  /**
   * The number of students allowed to be permanently assigned to a non-minor
   * section.
   */
  nonMinorSectionCap: number;
  /**
   * The default language for the course. Overrides the user's preferred
   * language if they navigate to the course in a language other than the
   * default.
   */
  courseDefaultLanguage?: LanguageCode;
  /**
   * Availability per hour across a section week for SL signup. Length equals sectionTimeDelta.
   */
  sectionLeaderSectionSignupAvailability?: boolean[];
  /**
   * The timestamp of the end of the section leader onboarding (prior to
   * training). Formatted as an ISO 8601 string as YYYY-MM-DDTHH:mm:ssXXX
   */
  slOnboardingEndDateTimestamp?: string;
  /**
   * Whether SL onboarding offers an optional backup weekly section time. See
   * `requireSlBackupSectionTime` on the course document.
   */
  requireSlBackupSectionTime: boolean;
  /**
   * Whether SL onboarding asks for a live section practice time. See
   * `hasSlLivePractice` on the course document.
   */
  hasSlLivePractice: boolean;
  /**
   * Whether the course runs sections for minors. See `hasMinorSections` on
   * the course document; omitted there means `true`.
   */
  hasMinorSections: boolean;
  /**
   * Whether student onboarding offers an optional backup section time. See
   * `allowStudentBackupSectionTime` on the course document.
   */
  allowStudentBackupSectionTime: boolean;
  /**
   * Whether student onboarding lets students say no time works or opt out of
   * sections. See `allowStudentSectionOptOut` on the course document.
   */
  allowStudentSectionOptOut: boolean;
  /**
   * Whether onboarding stays open with every student going to the Super
   * Section. See `postSectionAssignmentOnboarding` on the course document.
   */
  postSectionAssignmentOnboarding: boolean;
  /**
   * Whether a student's pick is a placement preference that may go beyond a
   * time's seats. See `allowStudentPicksBeyondCapacity` on the course
   * document.
   */
  allowStudentPicksBeyondCapacity: boolean;
  /**
   * What a student who missed their section is offered. See
   * `missedSectionMode` on the course document.
   */
  missedSectionMode: MissedSectionMode;
  /**
   * The number of weeks between the first and second section leader training
   * sessions. Training sessions are expected to happen on the same day and time
   * each week.
   */
  numWeeksBetweenTrainingSessions?: number;
  /**
   * The timestamp of the end of the student onboarding. Formatted as an ISO
   * 8601 string as YYYY-MM-DDTHH:mm:ssXXX
   */
  studentOnboardingEndDateTimestamp?: string;
  /**
   * The timestamp of the start of the student onboarding. Formatted as an ISO
   * 8601 string as YYYY-MM-DDTHH:mm:ssXXX
   */
  studentOnboardingStartDateTimestamp?: string;
  /**
   * The timestamp at which students are matched to their sections for the
   * current course offering. Used on the student Section page to tell
   * students who are not yet in a section when their section will be
   * assigned. When absent, the page falls back to the default message.
   * Formatted as an ISO 8601 string as YYYY-MM-DDTHH:mm:ssXXX.
   */
  sectionMatchingTimestamp?: string;
  /**
   * Whether students have been matched to their sections, as set by admins.
   * False until then, including when the course document has no value.
   */
  areSectionsMatched: boolean;
  /**
   * When TeachNow live teaching becomes available for the course. Formatted as
   * an ISO 8601 string as YYYY-MM-DDTHH:mm:ssXXX.
   */
  teachNowStartTimestamp?: string;
  /**
   * When TeachNow ends for the course. At/after this time every "Start
   * TeachNow" entry point is replaced with a "has ended" notice. Availability
   * is the half-open window `[teachNowStartTimestamp, teachNowEndTimestamp)`.
   * Absent means no scheduled end. Formatted as an ISO 8601 string as
   * YYYY-MM-DDTHH:mm:ssXXX.
   */
  teachNowEndTimestamp?: string;
}

const defaultData: CourseContextType = {
  courseId: '',
  courseName: '',
  courseStartDate: '',
  courseEndDate: '',
  nCourseWeeks: 0,
  isLoading: true,
  error: null,
  courseExists: false,
  courseMinRole: Role.UNREGISTERED,
  firstSectionTimestamp: '',
  _courseLessonWeek: 1 as CourseWeekId, // resets on monday
  _courseSectionWeek: 1 as SectionWeekId, // resets on friday
  courseFeatures: [],
  sectionTimeDelta: 0,
  sectionFeedbackDelayDays: SECTION_FEEDBACK_DELAY_DAYS_DEFAULT,
  studentJoinGraceMinutesAfterStart:
    STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT,
  shadowReviewDueWeekday: SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT,
  slFeatures: [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  isFeatureEnabled: (featureName: CourseFeature, isSl?: boolean) => false,
  getSectionsSnapshot: () => {
    return null;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  getFirstDayOfSection: (sectionIndex: number) => {
    return null;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  getFirstSectionOfWeekDate: (sectionWeekId: SectionWeekId) => {
    return null;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  getLastSectionOfWeekDate: (sectionWeekId: SectionWeekId) => {
    return null;
  },
  getLastSectionOfCourseDate: () => {
    return null;
  },
  storiesTypesenseIndex: '',
  roadmapList: [],
  roadmapIcons: {},
  updateRoadmapIcons: async () => {},
  lessonGroups: DEFAULT_LESSON_GROUPS,
  lockedPages: [],
  lockedPagesOpenToSls: [],
  courseCanvasAuthLink: '',
  isCodeInPlaceCourse: false,
  usesKarel: false,
  usesTeachNow: false,
  usesExternalZoom: false,
  opensSlidesOnSectionJoin: false,
  superSectionIds: [],
  superSectionLeaderIds: [],
  allowSlsToReferStudents: true,
  equationEditorEnabled: false,
  hiddenRichTextTools: [],
  forumGuidelinesPostId: undefined,
  hasMentors: true,
  courseHasGrades: false,
  isFoothill: false,
  minorSectionCap: 0,
  nonMinorSectionCap: 0,
  sectionLeaderSectionSignupAvailability: [],
  slOnboardingEndDateTimestamp: '',
  requireSlBackupSectionTime: false,
  hasSlLivePractice: true,
  hasMinorSections: true,
  allowStudentBackupSectionTime: false,
  allowStudentSectionOptOut: false,
  postSectionAssignmentOnboarding: false,
  allowStudentPicksBeyondCapacity: false,
  missedSectionMode: 'makeupSection',
  numWeeksBetweenTrainingSessions: 0,
  studentOnboardingEndDateTimestamp: '',
  studentOnboardingStartDateTimestamp: '',
  sectionMatchingTimestamp: '',
  areSectionsMatched: false,
  teachNowStartTimestamp: '',
  teachNowEndTimestamp: '',
  courseStyle: 'cip',
  courseTimezone: '',
  calendarConfig: resolveCourseCalendarConfig({course: null}),
  styleFeedbackExcludedAssignments: undefined,
  parentCourseId: undefined,
};

/**
 * TODO: there are two big refactors (one easy, one hard)
 *
 * 1. in some places in the database, weeks are 0 indexed, in others they are 1 indexed.
 *    This should be consistent (likely 0 indexed). When displaying to the user, we can add 1
 *
 * 2. This context uses a long list of useStates (each with their one setState). There is a
 *    great chance to reduce the amount of code.
 */

export const CourseContext = createContext<CourseContextType>(defaultData);

/**
 * Just the resolved course id, kept separate from {@link CourseContext} so
 * that consumers which only need the id (e.g. `useCourseId()`) don't
 * re-render on every course-data update. `CourseContext`'s value is a fresh
 * object on every internal state change (course features, weeks, ...); a
 * plain string prop only changes — and only triggers consumers — when the id
 * itself actually changes.
 */
export const CourseIdContext = createContext<CourseId>('');

/**
 * Reads and parses course data for the given courseId.
 */
export function useCourseContextForCourse(
  courseId: CourseId,
): CourseContextType {
  const setCourseNameAccessibility = useSetCourseName();
  const [courseName, setCourseName] = useState<string>('');
  const [courseTimezone, setCourseTimezone] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [courseStartDate, setCourseStartDate] = useState<string>('');
  const [courseEndDate, setCourseEndDate] = useState<string>('');
  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [nCourseWeeks, setnCourseWeeks] = useState<number>(0);
  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [firstSectionTimestamp, setFirstSectionTimeStamp] =
    useState<string>('');
  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [courseCanvasAuthLink, setCanvasCourseAuthLink] = useState<string>('');
  const [courseHasGrades, setCourseHasGrades] = useState<boolean>(false);
  const [courseFeatures, setCourseFeatures] = useState<CourseFeature[]>([]);
  const [slFeatures, setSlFeatures] = useState<CourseFeature[]>([]);
  const [superSectionIds, setSuperSectionIds] = useState<string[]>([]);
  const [superSectionLeaderIds, setSuperSectionLeaderIds] = useState<
    string[]
  >([]);
  const [sectionTimeDelta, setSectionTimeDelta] = useState<number>(0);
  const [sectionFeedbackDelayDays, setSectionFeedbackDelayDays] =
    useState<number>(SECTION_FEEDBACK_DELAY_DAYS_DEFAULT);
  const [
    studentJoinGraceMinutesAfterStart,
    setStudentJoinGraceMinutesAfterStart,
  ] = useState<number>(STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT);
  const [shadowReviewDueWeekday, setShadowReviewDueWeekday] = useState<number>(
    SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT,
  );
  const [minorSectionCap, setMinorSectionCap] = useState<number>(0);
  const [nonMinorSectionCap, setNonMinorSectionCap] = useState<number>(0);
  const [courseDefaultLanguage, setCourseDefaultLanguage] =
    useState<LanguageCode>();
  const [
    sectionLeaderSectionSignupAvailability,
    setSectionLeaderSectionSignupAvailability,
  ] = useState<boolean[]>([]);
  const [storiesTypesenseIndex, setStoriesTypesenseIndex] =
    useState<string>('');
  const [roadmapIcons, setRoadmapIcons] = useState<Record<string, string>>({});
  const [lessonGroups, setLessonGroups] =
    useState<readonly LessonGroup[]>(DEFAULT_LESSON_GROUPS);
  const [lockedPages, setLockedPages] = useState<
    readonly LockableCoursePage[]
  >([]);
  const [lockedPagesOpenToSls, setLockedPagesOpenToSls] = useState<
    readonly LockableCoursePage[]
  >([]);
  const [roadmapList, setRoadmapList] = useState<RoleFirebaseString[]>([
    'student',
    'sl',
  ]);
  const [isCodeInPlaceCourse, setIsCodeInPlaceCourse] =
    useState<boolean>(false);
  const [usesKarel, setUsesKarel] = useState<boolean>(false);
  const [usesTeachNow, setUsesTeachNow] = useState<boolean>(false);
  const [usesExternalZoom, setUsesExternalZoom] = useState<boolean>(false);
  const [opensSlidesOnSectionJoin, setOpensSlidesOnSectionJoin] =
    useState<boolean>(false);
  const [allowSlsToReferStudents, setAllowSlsToReferStudents] =
    useState<boolean>(true);
  const [equationEditorEnabled, setEquationEditorEnabled] =
    useState<boolean>(false);
  const [hiddenRichTextTools, setHiddenRichTextTools] = useState<
    readonly HideableRichTextTool[]
  >([]);
  const [forumGuidelinesPostId, setForumGuidelinesPostId] = useState<
    string | undefined
  >(undefined);
  const [hasMentors, setHasMentors] = useState<boolean>(true);
  const [numWeeksBetweenTrainingSessions, setNumWeeksBetweenTrainingSessions] =
    useState<number>(0);
  const [slOnboardingEndDateTimestamp, setSlOnboardingEndDateTimestamp] =
    useState<string>('');
  const [requireSlBackupSectionTime, setRequireSlBackupSectionTime] =
    useState<boolean>(false);
  const [hasSlLivePractice, setHasSlLivePractice] = useState<boolean>(true);
  const [hasMinorSections, setHasMinorSections] = useState<boolean>(true);
  const [allowStudentBackupSectionTime, setAllowStudentBackupSectionTime] =
    useState<boolean>(false);
  const [allowStudentSectionOptOut, setAllowStudentSectionOptOut] =
    useState<boolean>(false);
  const [
    postSectionAssignmentOnboarding,
    setPostSectionAssignmentOnboarding,
  ] = useState<boolean>(false);
  const [
    allowStudentPicksBeyondCapacity,
    setAllowStudentPicksBeyondCapacity,
  ] = useState<boolean>(false);
  const [missedSectionMode, setMissedSectionMode] =
    useState<MissedSectionMode>('makeupSection');
  const [
    studentOnboardingEndDateTimestamp,
    setStudentOnboardingEndDateTimestamp,
  ] = useState<string>('');
  const [
    studentOnboardingStartDateTimestamp,
    setStudentOnboardingStartDateTimestamp,
  ] = useState<string>('');
  const [sectionMatchingTimestamp, setSectionMatchingTimestamp] =
    useState<string>('');
  const [areSectionsMatched, setAreSectionsMatched] = useState<boolean>(false);
  const [teachNowStartTimestamp, setTeachNowStartTimestamp] =
    useState<string>('');
  const [teachNowEndTimestamp, setTeachNowEndTimestamp] = useState<string>('');
  const [courseStyle, setCourseStyle] = useState<CourseStyle>('cip');
  const [calendarConfig, setCalendarConfig] =
    useState<ResolvedCourseCalendarConfig>(() =>
      resolveCourseCalendarConfig({course: null}),
    );
  const [
    styleFeedbackExcludedAssignments,
    setStyleFeedbackExcludedAssignments,
  ] = useState<unknown>(undefined);
  const [parentCourseId, setParentCourseId] = useState<CourseId | undefined>(
    undefined,
  );

  const isFoothill = isFoothillCourse(courseId);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [courseExists, setCourseExists] = useState<boolean>(false);
  const [courseMinRole, setCourseMinRole] = useState<Role>(Role.UNREGISTERED);
  const {_courseLessonWeek, _courseSectionWeek} = useCourseWeeksInternal({
    isLoading,
    firstSectionTimestamp,
    sectionTimeDelta,
    courseStartDate,
  });

  const db = getFirestore();
  const courseDocRef = getCourseDocumentRef({courseId});

  const getFirstDayOfSection = (sectionIndex: number) => {
    // NOTE: this relies on `firstSectionTimestamp` being loaded.
    // Callers should guard on `CourseContext.isLoading` / `firstSectionTimestamp`
    // before invoking this function.
    if (!firstSectionTimestamp) {
      throw new Error('Timestamp not loaded yet');
    }
    const firstSectionOfCourseDate = new Date(firstSectionTimestamp);
    const firstSectionForTimeIndex = new Date(
      firstSectionOfCourseDate.getTime() + sectionIndex * ONE_HOUR_MS,
    );
    // add the timezone offset
    return firstSectionForTimeIndex;
  };

  function getFirstSectionOfWeekDate(sectionWeekId: SectionWeekId): Date {
    if (!firstSectionTimestamp) {
      throw new Error('First section timestamp is not loaded');
    }
    const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;
    // NOTE: sectionWeekId is 1 indexed, so we subtract 1 to get the correct offset
    return new Date(
      new Date(firstSectionTimestamp).getTime() +
        (sectionWeekId - 1) * WEEK_IN_MS,
    );
  }

  function getLastSectionOfWeekDate(sectionWeekId: SectionWeekId): Date {
    if (!firstSectionTimestamp) {
      throw new Error('First section timestamp is not loaded');
    }
    const firstSectionDate = getFirstSectionOfWeekDate(sectionWeekId);
    const lastSectionDate = new Date(
      firstSectionDate.getTime() + sectionTimeDelta * 60 * 60 * 1000,
    );
    return lastSectionDate;
  }

  function getLastSectionOfCourseDate(): Date {
    if (!firstSectionTimestamp) {
      throw new Error('First section timestamp is not loaded');
    }
    // nCourseWeeks, like sectionWeekId, is 1 indexed
    const lastSectionDate = getLastSectionOfWeekDate(
      nCourseWeeks as SectionWeekId,
    );
    return lastSectionDate;
  }

  useEffect(() => {
    if (!courseId) return;

    // The provider outlives a course switch (it is mounted in
    // CourseScopeLayout), so the previous course must read as loading, not as
    // this one, until this course's first snapshot lands.
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setIsLoading(true);
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setCourseExists(false);
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setError(null);

    // A live subscription rather than a one-shot read, so course-level edits
    // (roadmap icon defaults, admin settings) reach every open tab.
    const unsubscribe = onSnapshot(
      courseDocRef,
      courseDoc => {
        setCourseExists(courseDoc.exists());
        setSuperSectionIds([]);
        setSuperSectionLeaderIds([]);
        if (courseDoc.exists()) {
          const courseData = courseDoc.data();
          setCourseMinRole(
            courseData.minRole
              ? firebaseStringToRole(courseData.minRole)
              : Role.UNREGISTERED,
          );
          const safeCourseFeatures = courseData.features ?? [];
          const safeSlFeatures = courseData.slFeatures ?? [];
          setCourseName(courseData.name ?? '');
          setCourseStartDate(courseData.startDate ?? '');
          setnCourseWeeks(courseData.nWeeks ?? 0);
          // TODO: this is a hack to get the first section timestamp in the
          // correct timezone. We should remove this once we have a better
          // solution.
          setFirstSectionTimeStamp(courseData.firstSectionTimestamp ?? '');
          setCourseEndDate(courseData.endDate ?? '');
          setCourseFeatures(safeCourseFeatures);
          setSlFeatures(safeSlFeatures);
          try {
            setSectionTimeDelta(Number(courseData.sectionTimeDelta) ?? 0); // the number of hours of section a week
          } catch (e) {
            console.error('Error setting sectionTimeDelta', {
              courseData,
              e,
            });
            setSectionTimeDelta(0);
          }
          setSectionFeedbackDelayDays(
            courseData.sectionFeedbackDelayDays ??
              SECTION_FEEDBACK_DELAY_DAYS_DEFAULT,
          );
          setShadowReviewDueWeekday(
            courseData.shadowReviewDueWeekday ??
              SHADOW_REVIEW_DUE_WEEKDAY_DEFAULT,
          );
          setStudentJoinGraceMinutesAfterStart(
            courseData.studentJoinGraceMinutesAfterStart ??
              STUDENT_JOIN_GRACE_MINUTES_AFTER_START_DEFAULT,
          );
          setStoriesTypesenseIndex(courseData.storiesTypesenseIndex ?? '');
          setCanvasCourseAuthLink(courseData.canvasAuthLink ?? '');
          setCourseHasGrades(safeSlFeatures.includes('grades'));
          const verboseRoadmapList = [
            'student',
            'sl',
            ...(courseData.roadmapList ?? ['student', 'sl']),
          ];
          const uniqueRoadmap = array =>
            array.filter((v, i) => array.indexOf(v) === i);
          setRoadmapList(uniqueRoadmap(verboseRoadmapList));
          setRoadmapIcons(parseRoadmapIcons(courseData.roadmapIcons));
          setLessonGroups(resolveLessonGroups({course: courseData}));
          setLockedPages(resolveLockedPages({course: courseData}));
          setLockedPagesOpenToSls(
            resolveLockedPagesOpenToSls({course: courseData}),
          );
          setIsCodeInPlaceCourse(courseData.isCodeInPlaceCourse || false);
          setUsesKarel(courseData.usesKarel || true);
          setUsesTeachNow(courseData.usesTeachNow || true);
          // Defaults OFF: a course is CIP-managed Zoom unless it explicitly opts
          // into the instructor-provided link mode.
          setUsesExternalZoom(courseData.usesExternalZoom ?? false);
          setOpensSlidesOnSectionJoin(
            courseData.opensSlidesOnSectionJoin ?? false,
          );
          setSuperSectionIds(
            Array.isArray(courseData.superSectionIds)
              ? courseData.superSectionIds
              : [],
          );
          setSuperSectionLeaderIds(
            Array.isArray(courseData.superSectionLeaderIds)
              ? courseData.superSectionLeaderIds
              : [],
          );
          setAllowSlsToReferStudents(courseData.allowSlsToReferStudents ?? true);
          setEquationEditorEnabled(courseData.equationEditorEnabled ?? false);
          setHiddenRichTextTools(
            resolveHiddenRichTextTools({course: courseData}),
          );
          setForumGuidelinesPostId(
            courseData.forumGuidelinesPostId?.trim() || undefined,
          );
          setHasMentors(courseData.hasMentors ?? true);
          setMinorSectionCap(courseData.minorSectionCap || 10);
          setNonMinorSectionCap(courseData.nonMinorSectionCap || 10);
          setCourseDefaultLanguage(courseData.defaultLanguage);
          setParentCourseId(courseData.parentCourseId);
          // Default availability to all-true of length sectionTimeDelta if missing or wrong length
          const incomingAvailability: boolean[] =
            courseData.sectionLeaderSectionSignupAvailability ?? [];
          const desiredLen = Number(courseData.sectionTimeDelta) ?? 0;
          let normalizedAvailability: boolean[];
          if (
            !incomingAvailability ||
            incomingAvailability.length !== desiredLen
          ) {
            normalizedAvailability = Array.from(
              {length: desiredLen},
              () => true,
            );
          } else {
            normalizedAvailability = incomingAvailability.map(v => Boolean(v));
          }
          setSectionLeaderSectionSignupAvailability(normalizedAvailability);
          setNumWeeksBetweenTrainingSessions(
            courseData.numWeeksBetweenTrainingSessions ?? 0,
          );
          setSlOnboardingEndDateTimestamp(
            courseData.slOnboardingEndDateTimestamp ?? '',
          );
          setRequireSlBackupSectionTime(
            courseData.requireSlBackupSectionTime ?? false,
          );
          setHasSlLivePractice(courseData.hasSlLivePractice ?? true);
          setHasMinorSections(courseData.hasMinorSections ?? true);
          setAllowStudentBackupSectionTime(
            courseData.allowStudentBackupSectionTime === true,
          );
          setAllowStudentSectionOptOut(
            courseData.allowStudentSectionOptOut === true,
          );
          setPostSectionAssignmentOnboarding(
            courseData.postSectionAssignmentOnboarding === true,
          );
          setAllowStudentPicksBeyondCapacity(
            courseData.allowStudentPicksBeyondCapacity === true,
          );
          setMissedSectionMode(
            courseData.missedSectionMode === 'catchUpKit'
              ? 'catchUpKit'
              : 'makeupSection',
          );
          setStudentOnboardingEndDateTimestamp(
            courseData.studentOnboardingEndDateTimestamp ?? '',
          );
          setStudentOnboardingStartDateTimestamp(
            courseData.studentOnboardingStartDateTimestamp ?? '',
          );
          setSectionMatchingTimestamp(
            courseData.sectionMatchingTimestamp ?? '',
          );
          setAreSectionsMatched(courseData.areSectionsMatched === true);
          setTeachNowStartTimestamp(courseData.teachNowStartTimestamp ?? '');
          setTeachNowEndTimestamp(courseData.teachNowEndTimestamp ?? '');
          setCourseStyle(parseCourseStyle(courseData.courseStyle));
          setCourseTimezone(
            typeof courseData.timezone === 'string' ? courseData.timezone : '',
          );
          setCalendarConfig(resolveCourseCalendarConfig({course: courseData}));
          setStyleFeedbackExcludedAssignments(
            courseData.styleFeedbackExcludedAssignments,
          );
        }
        setIsLoading(false);
      },
      e => {
        setError(e);
        setIsLoading(false);
      },
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [courseId]);

  useEffect(() => {
    setCourseNameAccessibility(courseName);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [courseName]);

  const isFeatureEnabled = (
    featureName: CourseFeature,
    isSl: boolean = false,
  ) => {
    const features = isSl ? slFeatures : courseFeatures;
    return features.includes(featureName);
  };

  const getSectionsSnapshot = async () => {
    // fetches sections for this course
    const sectionRef = collection(db, 'sections');
    const q = query(sectionRef, where('courseId', '==', courseId));
    const querySnapshot = await getDocs(q);
    return querySnapshot;
  };

  // The course-document subscription above is the only writer of
  // `roadmapIcons`; this just persists, and the snapshot echoes it back.
  const updateRoadmapIcons = async (icons: Record<string, string>) => {
    await updateDoc(courseDocRef, {roadmapIcons: icons});
  };

  return {
    courseId,
    isLoading,
    error,
    courseExists,
    courseMinRole,
    parentCourseId,
    courseName,
    courseTimezone,
    courseStartDate,
    courseEndDate,
    nCourseWeeks,
    firstSectionTimestamp,
    courseFeatures,
    slFeatures,
    _courseSectionWeek,
    _courseLessonWeek,
    isFeatureEnabled,
    getSectionsSnapshot,
    sectionTimeDelta,
    sectionFeedbackDelayDays,
    studentJoinGraceMinutesAfterStart,
    shadowReviewDueWeekday,
    getFirstDayOfSection,
    getFirstSectionOfWeekDate,
    getLastSectionOfWeekDate,
    getLastSectionOfCourseDate,
    storiesTypesenseIndex,
    roadmapList,
    roadmapIcons,
    updateRoadmapIcons,
    lessonGroups,
    lockedPages,
    lockedPagesOpenToSls,
    courseCanvasAuthLink,
    isCodeInPlaceCourse,
    usesKarel,
    usesTeachNow,
    usesExternalZoom,
    opensSlidesOnSectionJoin,
    superSectionIds,
    superSectionLeaderIds,
    allowSlsToReferStudents,
    equationEditorEnabled,
    hiddenRichTextTools,
    forumGuidelinesPostId,
    hasMentors,
    courseHasGrades,
    isFoothill,
    minorSectionCap,
    nonMinorSectionCap,
    courseDefaultLanguage,
    sectionLeaderSectionSignupAvailability,
    slOnboardingEndDateTimestamp,
    requireSlBackupSectionTime,
    hasSlLivePractice,
    hasMinorSections,
    allowStudentBackupSectionTime,
    allowStudentSectionOptOut,
    postSectionAssignmentOnboarding,
    allowStudentPicksBeyondCapacity,
    missedSectionMode,
    numWeeksBetweenTrainingSessions,
    studentOnboardingEndDateTimestamp,
    studentOnboardingStartDateTimestamp,
    sectionMatchingTimestamp,
    areSectionsMatched,
    teachNowStartTimestamp,
    teachNowEndTimestamp,
    courseStyle,
    calendarConfig,
    styleFeedbackExcludedAssignments,
  };
}

/**
 * Hook to check if section leader onboarding is closed for a course. Uses
 * real-time server time to determine if the onboarding end timestamp has
 * passed.
 *
 * @param slOnboardingEndDateTimestamp - The end timestamp for SL onboarding
 * @returns boolean - true if onboarding is closed, false if open or no
 * timestamp set
 */
export function useIsSLOnboardingClosed(
  slOnboardingEndDateTimestamp?: string,
): boolean {
  // We don't need to be that precise, so make it a long refresh rate to avoid
  // unnecessary re-renders.
  const serverTimeMs = useServerTime(15 * ONE_MINUTE_MS);

  if (!slOnboardingEndDateTimestamp || !serverTimeMs) {
    return false;
  }

  return new Date(slOnboardingEndDateTimestamp) < new Date(serverTimeMs);
}

/**
 * Provides a CourseContext for the user's current course.
 * This is a wrapper around the useCourseContextForCourse hook.
 *
 * @param courseId - The resolved course id, supplied when this provider is
 *   mounted ABOVE the `:courseId` route (see `CourseScopeLayout` in
 *   Router.tsx, which also resolves the `/apply/:targetCourseId` short link
 *   to the target's parent course — an ancestor layout cannot read that
 *   param itself). When omitted, falls back to the `:courseId` URL param,
 *   the right default for providers mounted directly on a `:courseId` route
 *   (e.g. the showcase route).
 */
export function CourseProvider({
  children,
  courseId: courseIdOverride,
}: {
  children: React.ReactNode;
  courseId?: CourseId;
}) {
  const {courseId: courseIdParam} = useParams();
  const courseId = courseIdOverride ?? courseIdParam;
  const courseContext = useCourseContextForCourse(courseId);

  return (
    <CourseIdContext.Provider value={courseId ?? ''}>
      <CourseContext.Provider value={courseContext}>
        {children}
      </CourseContext.Provider>
    </CourseIdContext.Provider>
  );
}

export const isFoothillCourse = (courseId: CourseId) => {
  return (
    [
      'foothill-cs49',
      'cs49-f24',
      'cs49-w24',
      'cs49-s25',
      'cs49-f25-dev',
      'cs49-f25',
      'cs49-w26',
    ].includes(courseId) ||
    (courseId && courseId.startsWith('cs49-'))
  );
};

export const useDoesCourseSupportExperiencedStudents = (): {
  doesSupportExperiencedStudents: boolean;
  isLoading: boolean;
} => {
  const {roadmapList, isLoading} = useContext(CourseContext);
  if (isLoading) return {doesSupportExperiencedStudents: false, isLoading};
  return {
    doesSupportExperiencedStudents: roadmapList.includes(
      roleToFirebaseString(Role.EXPERIENCED_STUDENT),
    ),
    isLoading,
  };
};
